import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';

import { sbq, filterDeleted, throwOnError } from '../../shared/utils/supabaseQuery';
import { applyOwnerFields } from '../../shared/utils/applyOwnerFields';
import { getDefaultOverviewItems } from '../../shared/utils/contentItems';
import {
  placementToAnchorPair,
  reorderByPlacement,
  type ReorderPlacement,
} from '../../shared/utils/reorder';
import { lessonsApi } from '../lessons/lessonsApi';
import { resourcesApi } from '../resources/resourcesApi';

import { Unit, UnitContent, CreateUnitRequest, UpdateUnitRequest } from '@/shared/types/unit';

type MoveUnitOverviewItemArgs = {
  unitId: string;
  itemId: string;
  placement: ReorderPlacement;
};

function applyOptimisticUnitReorder(draft: Unit[], unitId: string, placement: ReorderPlacement) {
  const reordered = reorderByPlacement(draft, unitId, placement);
  reordered.forEach((unit, index) => {
    unit.position = (index + 1) * 1000;
  });
  draft.splice(0, draft.length, ...reordered);
}

export const unitsApi = createApi({
  reducerPath: 'unitsApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Unit'],
  endpoints: (builder) => ({
    // Get a single unit by ID
    getUnitById: builder.query<Unit, string>({
      queryFn: sbq(async (sb, unitId) => {
        const data = throwOnError(
          await sb
            .from('units')
            .select(
              '*, owner:users(name, avatar_url), lessons(id, title, description, owner_id, is_public, is_deleted, content, position, created_at, updated_at, owner:users(name, avatar_url))'
            )
            .eq('id', unitId)
            .order('position', { referencedTable: 'lessons', ascending: true })
            .single()
        );

        // Filter out deleted lessons
        const lessons = filterDeleted((data as any)?.lessons || []).map((lesson: any) => ({
          ...applyOwnerFields(lesson),
          content: lesson?.content ?? {},
        }));

        // Attach owner_name for frontend compatibility
        return {
          ...applyOwnerFields(data as any),
          lessons,
          content: (data as any)?.content ?? {},
        } as Unit;
      }, 'Unit'),
      providesTags: (result, _error, unitId) => (result ? [{ type: 'Unit', id: unitId }] : []),
    }),

    // Get units for a specific course
    getUnitsByCourseId: builder.query<Unit[], string>({
      queryFn: sbq(async (sb, courseId) => {
        const data = throwOnError(
          await sb
            .from('units')
            .select('*, owner:users(name, avatar_url)')
            .eq('course_id', courseId)
            .eq('is_deleted', false)
            .order('position', { ascending: true })
        );

        // Attach owner_name for frontend compatibility
        return (data || []).map((unit: any) => ({
          ...applyOwnerFields(unit),
          content: unit?.content ?? {},
        }));
      }, 'Units'),
      providesTags: ['Unit'],
    }),

    // Get units owned by the current user
    getMyUnits: builder.query<Unit[], string>({
      queryFn: sbq(async (sb, userId) => {
        const data = throwOnError(
          await sb
            .from('units')
            .select('*, owner:users(name, avatar_url), course:courses(title)')
            .eq('owner_id', userId)
            .eq('is_deleted', false)
            .order('updated_at', { ascending: false })
        );

        // Attach owner_name and course_title for frontend compatibility
        return (data || []).map((unit: any) => ({
          ...applyOwnerFields(unit),
          course_title: unit?.course?.title || '',
          content: unit?.content ?? {},
        }));
      }, 'Units'),
      providesTags: ['Unit'],
    }),

    // Create a new unit
    createUnit: builder.mutation<Unit, CreateUnitRequest>({
      queryFn: sbq(async (sb, unitData) => {
        const now = new Date().toISOString();

        const content: UnitContent = {
          schemaVersion: '1',
          overview: getDefaultOverviewItems('unit'),
        };

        const newUnit = {
          ...unitData,
          content,
          created_at: now,
          updated_at: now,
        };

        const result = throwOnError(await sb.from('units').insert([newUnit]).select().single());

        return result as Unit;
      }, 'Unit'),
      invalidatesTags: ['Unit'],
    }),

    // Update a unit
    updateUnit: builder.mutation<Unit, UpdateUnitRequest>({
      queryFn: sbq(async (sb, updateData) => {
        const { id, ...fields } = updateData;
        if (!id) throw new Error('Unit ID required');

        const now = new Date().toISOString();
        const updateFields = { ...fields, updated_at: now };

        const result = throwOnError(
          await sb.from('units').update(updateFields).eq('id', id).select().single()
        );

        return result as Unit;
      }, 'Unit'),
      invalidatesTags: ['Unit'],
    }),

    // Delete a unit
    deleteUnit: builder.mutation<void, string>({
      queryFn: sbq(async (sb, unitId) => {
        if (!unitId) throw new Error('Unit ID required');

        const { error } = await sb.rpc('soft_delete_unit', { p_unit_id: unitId });
        if (error) throw error;

        return undefined;
      }, 'Unit'),
      invalidatesTags: ['Unit'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(lessonsApi.util.invalidateTags(['Lesson']));
          dispatch(resourcesApi.util.invalidateTags(['Resource', 'ResourceList']));
        } catch {
          // delete failed; nothing to invalidate
        }
      },
    }),

    // Move a unit to a new position
    moveUnit: builder.mutation<
      void,
      { unitId: string; courseId: string; placement: ReorderPlacement }
    >({
      queryFn: sbq(async (sb, { unitId, placement }) => {
        const { beforeId, afterId } = placementToAnchorPair(placement);
        const { error } = await sb.rpc('move_unit', {
          p_id: unitId,
          p_before: beforeId,
          p_after: afterId,
        });
        if (error) throw error;
        return undefined;
      }, 'Unit'),
      invalidatesTags: ['Unit'],
      async onQueryStarted({ unitId, courseId, placement }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          unitsApi.util.updateQueryData('getUnitsByCourseId', courseId, (draft) => {
            applyOptimisticUnitReorder(draft, unitId, placement);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    moveUnitOverviewItem: builder.mutation<void, MoveUnitOverviewItemArgs>({
      queryFn: sbq(async (sb, { unitId, itemId, placement }) => {
        const { error } = await sb.rpc('move_unit_overview_item', {
          p_unit_id: unitId,
          p_item_id: itemId,
          p_position: placement.position,
          p_anchor_id: placement.anchorId,
        });
        if (error) throw error;
        return undefined;
      }, 'Unit'),
      invalidatesTags: ['Unit'],
      async onQueryStarted({ unitId, itemId, placement }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          unitsApi.util.updateQueryData('getUnitById', unitId, (draft) => {
            const content = draft.content ?? { schemaVersion: '1' };
            draft.content = {
              ...content,
              schemaVersion: '1',
              overview: reorderByPlacement(content.overview ?? [], itemId, placement),
            };
            draft.updated_at = new Date().toISOString();
          })
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export const {
  useGetUnitByIdQuery,
  useGetUnitsByCourseIdQuery,
  useGetMyUnitsQuery,
  useCreateUnitMutation,
  useUpdateUnitMutation,
  useDeleteUnitMutation,
  useMoveUnitMutation,
  useMoveUnitOverviewItemMutation,
} = unitsApi;
